import { useEffect, useState } from "react";
import { doc, onSnapshot, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase/client";
import type { User } from "firebase/auth";
import { readPlanId, readPlanStatus } from "../services/plan";

type Tier = "free" | "trial" | "pro" | "premium";
type PlanStatus =
  | "free"
  | "trialing"
  | "active"
  | "pending"
  | "canceled"
  | "past_due"
  | "inactive"
  | "expired"
  | "none";

export type UserPlanInfo = {
  tier: Tier;
  status: PlanStatus;
  role?: string;
  trialDaysRemaining: number | null;
  trialExpiresAt: number | null;
  trialUsed: boolean;
};

function normalizeTier(value: any): Tier {
  if (value === "trial") return "trial";
  if (value === "basic") return "pro"; // legado: basic → pro
  if (value === "pro") return "pro";
  if (value === "premium") return "premium";
  return "free";
}

function normalizeStatus(value: any): PlanStatus {
  if (value === "free") return "free";
  if (value === "active") return "active";
  if (value === "trialing") return "trialing";
  if (value === "pending") return "pending";
  if (value === "canceled") return "canceled";
  if (value === "past_due") return "past_due";
  if (value === "inactive") return "inactive";
  if (value === "expired") return "expired";
  return "inactive";
}

function toMillisFlexible(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const p = Date.parse(value);
    return Number.isNaN(p) ? null : p;
  }
  if (value && typeof value === "object") {
    const ts = value as { toMillis?: () => number; seconds?: unknown };
    if (typeof ts.toMillis === "function") return ts.toMillis();
    const sec = Number((ts as any).seconds);
    if (Number.isFinite(sec)) return sec * 1000;
  }
  return null;
}

function computeTrialDaysRemaining(expiresAtMs: number | null): number | null {
  if (expiresAtMs === null) return null;
  const diff = expiresAtMs - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function useUserPlan(user: User | null) {
  const [plan, setPlan] = useState<UserPlanInfo>({
    tier: "free",
    status: "inactive",
    trialDaysRemaining: null,
    trialExpiresAt: null,
    trialUsed: false,
  });

  const [loadingPlan, setLoadingPlan] = useState<boolean>(!!user);

  useEffect(() => {
    if (!user) {
      setPlan({
        tier: "free",
        status: "none",
        role: undefined,
        trialDaysRemaining: null,
        trialExpiresAt: null,
        trialUsed: false,
      });
      setLoadingPlan(false);
      return;
    }

    setLoadingPlan(true);
    const ref = doc(db, "users", user.uid);

    const unsub = onSnapshot(
      ref,
      async (snap) => {
        const data: any = snap.exists() ? snap.data() : null;
        const rawTier = readPlanId(data);
        const rawStatus = readPlanStatus(data);

        let tier = normalizeTier(rawTier);
        let status = normalizeStatus(rawStatus);

        // Ler campos de trial
        const trialExpiresAtRaw = data?.trialExpiresAt ?? data?.trialEndsAt ?? null;
        const trialExpiresAt = toMillisFlexible(trialExpiresAtRaw);
        const trialUsed: boolean = Boolean(data?.trialUsed);

        // Verificar expiração de trial
        if (tier === "trial" && trialExpiresAt !== null && trialExpiresAt < Date.now()) {
          console.log("[TRIAL][expired] uid=", user.uid);
          tier = "free";
          status = "free";
          // Atualizar Firestore em background (não bloqueia UI)
          updateDoc(ref, {
            plan: "free",
            planTier: "free",
            tier: "free",
            role: "free",
            subscriptionStatus: "free",
            trialUsed: true,
            updatedAt: serverTimestamp(),
          }).catch((err) =>
            console.error("[TRIAL][expire_write_failed]", err?.message)
          );
        }

        const trialDaysRemaining =
          tier === "trial" ? computeTrialDaysRemaining(trialExpiresAt) : null;

        setPlan({
          tier,
          status,
          role: data?.role,
          trialDaysRemaining,
          trialExpiresAt,
          trialUsed,
        });

        setLoadingPlan(false);
      },
      (error) => {
        console.error("Erro ao carregar plano do usuário:", error);
        setPlan({
          tier: "free",
          status: "inactive",
          role: undefined,
          trialDaysRemaining: null,
          trialExpiresAt: null,
          trialUsed: false,
        });
        setLoadingPlan(false);
      }
    );

    return () => unsub();
  }, [user]);

  return { plan, loadingPlan };
}
